/**
 * "2 hours ago" — the product's one relative-time vocabulary.
 *
 * Written for `settings-members.html` in Story 4.1 and lifted out of that page
 * by Story 6.1, which needs the same words for a different noun: the members
 * table says "Active 2 hours ago" and a funnel card says "Edited 2 hours ago".
 * Two copies of this would drift at the boundaries — where "yesterday" stops
 * and "4 days ago" starts — and the drift would show as two screens disagreeing
 * about the same timestamp.
 *
 * `now` is a parameter so a test can pin it. The screens pass the real clock:
 * a frozen fixture instant makes every row read "just now" the moment the
 * machine's timezone runs ahead of the constant.
 */
const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

export function relative(at: number, now = Date.now()): string {
  const ago = Math.max(0, now - at)
  if (ago < 5 * MINUTE) return 'just now'
  if (ago < HOUR) {
    const minutes = Math.round(ago / MINUTE)
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  }
  if (ago < DAY) {
    const hours = Math.round(ago / HOUR)
    return `${hours} hour${hours === 1 ? '' : 's'} ago`
  }
  if (ago < 2 * DAY) return 'yesterday'
  if (ago < 7 * DAY) return `${Math.round(ago / DAY)} days ago`
  if (ago < 14 * DAY) return 'last week'
  return `${Math.floor(ago / (7 * DAY))} weeks ago`
}
